/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { useDocumentVisibility } from '@vueuse/core'
import debug from '../../utils/console'
import type {
  AssertiveLogImpressionEvent,
  AssertiveYieldOptions,
  AyData,
  AyLogImpressionEvent,
} from '../../types/assertive'

const logger = debug.withTag('composables:useAssertiveYield')

export function useAssertiveYield(options?: Partial<AssertiveYieldOptions>) {
  const { $assertive, $ayManagerEnv } = useNuxtApp()
  // $ayLoaded is the load promise now, so ask the manager itself. Callers read
  // this once per event and must not block on the load.
  if (!import.meta.client || !$ayManagerEnv?.versionInfo) {
    return {
      assertive: null,
      ayManagerEnv: null,
      ayData: null,
    }
  }

  const previousCalledAction = ref<string | null>(null)

  window.addEventListener(
    'assertive_logImpression',
    async (e: AssertiveLogImpressionEvent) => {
      try {
        const payload = e?.data?.payload as AyLogImpressionEvent

        const { unfilled, auctionId } = payload
        const isNewRequest = previousCalledAction.value !== auctionId

        if (options?.onAdFulfilled && !unfilled && auctionId && isNewRequest) {
          previousCalledAction.value = auctionId
          await options?.onAdFulfilled(payload)
        }
      } catch (e) {
        logger.error(e)
      }
    }
  )

  const pageVisibility = useDocumentVisibility()
  watch(pageVisibility, (value) => {
    const isPageVisible = value === 'visible'
    window.isPageVisible = isPageVisible

    if (isPageVisible) {
      $ayManagerEnv?.cmd.push(() => {
        $ayManagerEnv?.changePage()
        $ayManagerEnv?.dispatchManualEvent()
      })
    }
  })

  const ayData: AyData = {
    version: $ayManagerEnv?.versionInfo?.versionId ?? 0,
    deployment: $ayManagerEnv?.versionInfo?.deployId ?? '',
    deploymentPercentage: $ayManagerEnv?.versionInfo?.percentage ?? 0,
  }

  return {
    assertive: $assertive,
    ayManagerEnv: $ayManagerEnv,
    ayData,
  }
}

/**
 * Assertive Yield only looks for divs when it first loads,
 * for SPAs or elements that interactively change, we need to reevaluate the page
 * using the `changePage` and `dispatchManualEvent` functions suggested from
 * their docs.
 **/
// TODO: @alexannmill look into this, i don't think we need this anymore since in app migration
export function useAssertiveYieldReevaluate() {
  const { $ayManagerEnv } = useNuxtApp()
  if (!$ayManagerEnv) return

  $ayManagerEnv?.cmd?.push(() => {
    $ayManagerEnv?.changePage()
  })
}

export const logAdImpressionToAY = (
  data: Partial<AyLogImpressionEvent> | null
) => {
  if (!import.meta.client || !data) return

  window.assertiveQueue = window?.assertiveQueue || []
  window.assertiveQueue.push(function () {
    if (
      window.assertive &&
      typeof window.assertive.logCustomImpression === 'function'
    ) {
      window.assertive.logCustomImpression(data)
    }
  })
}

export const logAdClickToAY = (slotId: string) => {
  if (!import.meta.client || !slotId) return

  window.assertiveQueue = window?.assertiveQueue || []
  window.assertiveQueue.push(function () {
    if (
      window.assertive &&
      typeof window.assertive.logCustomClick === 'function'
    ) {
      window.assertive.logCustomClick(slotId)
    }
  })
}

export default useAssertiveYield
